import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Copy, Check, Clock, Loader2, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copié`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-slate-400">{label}</div>
        <div className="font-medium text-slate-900 break-all">{value}</div>
      </div>
      <button type="button" onClick={copy} className="shrink-0 w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors" title="Copier" data-testid={`copy-${label}`}>
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const BankTransferDialog = ({ data, onClose, api }) => {
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  if (!data) return null;

  const handleConfirm = async () => {
    if (!api || !data.transaction_id) { onClose(); return; }
    try {
      setSubmitting(true);
      await axios.post(`${api}/payments/orders/${data.transaction_id}/confirm-transfer`, {}, { withCredentials: true });
      toast.success("Votre bon de commande a été envoyé sur votre profil.");
      onClose();
    } catch (e) {
      toast.error("Impossible d'envoyer le bon de commande. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!api || !data.transaction_id) { onClose(); return; }
    try {
      setCancelling(true);
      await axios.post(`${api}/payments/orders/${data.transaction_id}/cancel`, {}, { withCredentials: true });
      toast.success("Commande annulée.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'annuler la commande.");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="bank-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Landmark className="w-5 h-5 text-orange-500" />Payer par virement bancaire</DialogTitle>
          <DialogDescription>Scannez le QR code avec votre application bancaire : le virement est pré-rempli.</DialogDescription>
        </DialogHeader>

        {data.order_number && (
          <div className="rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm flex items-center justify-between" data-testid="transfer-order-number">
            <span className="text-slate-300">Bon de commande</span>
            <span className="font-mono font-bold">{data.order_number}</span>
          </div>
        )}

        {Array.isArray(data.leads) && data.leads.length > 0 && (
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="transfer-leads-list">
            {data.leads.map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-4 py-2 text-sm" data-testid={`transfer-lead-${i}`}>
                <span className="text-slate-600 min-w-0 truncate">{l.lead_number} · {l.city}</span>
                <span className="font-medium text-slate-900 shrink-0">{Number(l.price).toFixed(2)}€</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col items-center">
          <div className="bg-white p-3 rounded-xl border border-slate-200">
            <img src={data.qr} alt="QR code de paiement SEPA" className="w-52 h-52" data-testid="transfer-qr-image" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-3" data-testid="transfer-amount">{Number(data.amount).toFixed(2)}€</p>
          <p className="text-xs text-slate-400 mb-3">Choisissez le <strong>virement instantané</strong> dans votre app pour un traitement plus rapide.</p>
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-200 px-4">
          <CopyRow label="Bénéficiaire" value={data.beneficiary} />
          <CopyRow label="IBAN" value={data.iban} />
          <CopyRow label="Communication" value={data.reference} />
        </div>

        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1" data-testid="transfer-pending-note">
          <Clock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Dès que vous cliquez sur <strong>« J'ai effectué le virement »</strong>, votre <strong>bon de commande</strong> est déposé sur votre profil et envoyé par e-mail (il vaut engagement de paiement). Les coordonnées du client vous seront transmises <strong>dès validation de votre paiement</strong>. La facture récapitulative vous parviendra <strong>en fin de mois par e-mail et via Peppol</strong> (pour les pays concernés).</span>
        </div>

        <Button onClick={handleConfirm} disabled={submitting || cancelling} className="w-full mt-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="transfer-close">
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi du bon de commande…</> : "J'ai effectué le virement"}
        </Button>
        <Button variant="outline" onClick={handleCancel} disabled={submitting || cancelling} className="w-full gap-2 border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="cancel-order">
          {cancelling ? <><Loader2 className="w-4 h-4 animate-spin" />Annulation…</> : <><ArrowLeft className="w-4 h-4" />Retour — annuler la commande</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
